import { Dialog } from '@radix-ui/themes';
import React, { useState } from 'react';
import type { Match, FeedbackReasonTag } from '../../types/match';
import { Button } from '../common';
import { api } from '../../utils';
import { DoubleCheck, Xmark } from 'iconoir-react';
import DeclineModal from './DeclineModal';
import { useSubmitMatchFeedbackWithReasons } from '../../hooks/useMatch';
import toast from 'react-hot-toast';
import { Toaster } from '../common';

interface MatchActionsProps {
  match: Match;
  onApprove?: () => Promise<void>;
  onReject?: (open: boolean) => Promise<void>;
  variant: 'horizontal' | 'vertical';
  openReviewModal?: boolean;
  setOpenReviewModal?: (open: boolean) => void;
}

const MatchActions = ({
  match,
  onApprove,
  onReject,
  variant,
  openReviewModal,
  setOpenReviewModal,
}: MatchActionsProps) => {
  const { id, other_user } = match;

  const [isRejecting, setIsRejecting] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  // Phase 2.1: Feedback with reasons
  const { mutateAsync: submitFeedbackWithReasons, isPending: isSubmittingFeedback } =
    useSubmitMatchFeedbackWithReasons();

  const handleApprove = async () => {
    setIsApproving(true);
    api.post('/analytics/track', {
      event_type: 'match_approve',
      event_category: 'matching',
      event_data: { match_id: id },
    }).catch(() => {});
    try {
      // Note: the parent's onApprove already shows an accurate "You have
      // approved {name}!" toast, and the card then reflects the true next
      // state (either the chat button if it became mutual, or "waiting for
      // them to approve before you can message"). We deliberately do NOT
      // claim "you can now message" here: connecting usually moves the match
      // to awaiting_other, where messaging is not yet possible.
      await onApprove?.();
    } catch (error: any) {
      toast.custom(t => (
        <Toaster
          message={error?.message || 'Failed to approve match'}
          type='error'
          id={t.id}
        />
      ));
    } finally {
      setIsApproving(false);
    }
  };

  // Updated handler to use structured feedback with reason tags
  const handleDeclineWithReasons = async (
    reasonTags: FeedbackReasonTag[],
    reasonText: string
  ) => {
    setIsRejecting(true);
    // Track decline event for analytics funnel
    api.post('/analytics/track', {
      event_type: 'match_decline',
      event_category: 'matching',
      event_data: { match_id: id, reason_tags: reasonTags },
    }).catch(() => {});
    try {
      await submitFeedbackWithReasons({
        match_id: id,
        status: 'declined',
        reason_tags: reasonTags,
        reason_text: reasonText || undefined,
      });

      toast.custom(t => (
        <Toaster
          message={`Thanks for your feedback on ${other_user.name}`}
          type='success'
          id={t.id}
        />
      ));

      setOpenReviewModal?.(false);
      // Trigger parent callback for any additional cleanup
      await onReject?.(false);
    } catch (error: any) {
      toast.custom(t => (
        <Toaster
          message={error?.message || 'Failed to submit feedback'}
          type='error'
          id={t.id}
        />
      ));
    } finally {
      setIsRejecting(false);
    }
  };

  const handleCancel = () => {
    setOpenReviewModal?.(false);
  };

  // BUG FIX #4: Use direct onClick instead of Dialog.Trigger for consistent modal behavior
  // Dialog.Trigger can cause inconsistent open/close when combined with controlled state
  const handleOpenModal = () => {
    setOpenReviewModal?.(true);
  };

  return (
    <div
      className={`flex gap-2 my-1 ${variant === 'horizontal' ? 'justify-end' : 'items-center'}`}
    >
      <Dialog.Root open={openReviewModal} onOpenChange={setOpenReviewModal}>
        {/* Removed Dialog.Trigger - using direct onClick for consistent behavior */}
        <Button
          size='sm'
          variant='outline'
          isLoading={isRejecting}
          className={`${variant === 'horizontal' ? 'w-full md:w-fit' : 'w-full'}`}
          onClick={handleOpenModal}
        >
          <Xmark className='w-6 h-6' /> Pass
        </Button>
        <Dialog.Content>
          {/* Phase 2.1: Use DeclineModal with structured reason tags */}
          <DeclineModal
            matchId={id}
            otherUserName={other_user.name}
            onSubmit={handleDeclineWithReasons}
            onCancel={handleCancel}
            isLoading={isSubmittingFeedback || isRejecting}
          />
        </Dialog.Content>
      </Dialog.Root>
      <Button
        size='sm'
        className={`${variant === 'horizontal' ? 'w-full md:w-fit' : 'w-full'}`}
        isLoading={isApproving}
        onClick={handleApprove}
      >
        <DoubleCheck className='w-6 h-6' /> Connect
      </Button>
    </div>
  );
};

export default MatchActions;
